// File: src/pages/Terms.jsx

import React, {
  useEffect,
  useMemo,
  useState,
} from 'react';

import { Link } from 'react-router-dom';
import { IoIosArrowBack } from 'react-icons/io';

import SmsPhoneNumberScreenshot from './../assets/01_phone_number_step.png';
import SmsConsentDeclinedScreenshot from './../assets/02_sms_consent_declined.png';
import SmsTermsOptionalScreenshot from './../assets/03_terms_sms_optional.png';
import SmsSettingsOffScreenshot from './../assets/04_settings_text_alerts_off.png';

import { USER_TERMS_OF_SERVICE } from '../components/legal/userLegalText';

const PAGE_SECTIONS = [
  {
    id: 'overview',
    label: 'Overview',
    startsAt: null,
  },
  {
    id: 'eligibility-account',
    label: 'Eligibility & Account',
    startsAt: '1. ELIGIBILITY',
  },
  {
    id: 'safety-conduct',
    label: 'Safety & Conduct',
    startsAt: '4. ASSUMPTION OF RISK; OFFLINE RESPONSIBILITY',
  },
  {
    id: 'sms-notifications',
    label: 'SMS & Notifications',
    startsAt: '7. COMMUNICATIONS; SMS; NOTIFICATIONS',
  },
  {
    id: 'reservations-attendance',
    label: 'Reservations & Attendance',
    startsAt: '9. FREE MEMBERSHIP; RESERVATIONS; ATTENDANCE',
  },
  {
    id: 'gatherings',
    label: 'Gatherings',
    startsAt: '10. GATHERINGS; HOSTS; LOCATIONS',
  },
  {
    id: 'liability',
    label: 'Liability',
    startsAt: '12. DISCLAIMERS',
  },
  {
    id: 'legal',
    label: 'Disputes & Legal',
    startsAt:
      '16. DISPUTE RESOLUTION; ARBITRATION; CLASS ACTION WAIVER',
  },
  {
    id: 'sms-consent',
    label: 'SMS Consent Flow',
    startsAt: null,
  },
];

export default function Terms() {
  const [activeSection, setActiveSection] =
    useState('overview');

  const [showSideNav, setShowSideNav] = useState(
    typeof window !== 'undefined'
      ? window.innerWidth >= 900
      : true
  );

  const [compactNav, setCompactNav] = useState(
    typeof window !== 'undefined'
      ? window.innerWidth < 700
      : false
  );

  const anchorByHeading = useMemo(() => {
    const map = {};

    PAGE_SECTIONS.forEach((section) => {
      if (section.startsAt) {
        map[section.startsAt] = section.id;
      }
    });

    return map;
  }, []);

  // -----------------------------------------
  // Responsive left navigation
  // -----------------------------------------
  useEffect(() => {
    const handleResize = () => {
      setShowSideNav(window.innerWidth >= 900);
      setCompactNav(window.innerWidth < 700);
    };

    handleResize();

    window.addEventListener(
      'resize',
      handleResize
    );

    return () => {
      window.removeEventListener(
        'resize',
        handleResize
      );
    };
  }, []);

  // -----------------------------------------
  // Track active Terms section
  // -----------------------------------------
  useEffect(() => {
    const handleScroll = () => {
      const marker = 145;
      let current = 'overview';

      PAGE_SECTIONS.forEach(({ id }) => {
        const el =
          document.getElementById(id);

        if (!el) return;

        if (
          el.getBoundingClientRect().top <=
          marker
        ) {
          current = id;
        }
      });

      setActiveSection(current);
    };

    handleScroll();

    window.addEventListener(
      'scroll',
      handleScroll,
      {
        passive: true,
      }
    );

    return () => {
      window.removeEventListener(
        'scroll',
        handleScroll
      );
    };
  }, []);

  const goToSection = (id) => {
    const el =
      document.getElementById(id);

    if (!el) return;

    el.scrollIntoView({
      behavior: 'smooth',
      block: 'start',
    });
  };

  // -----------------------------------------
  // Render legal copy with hierarchy
  // -----------------------------------------
  const renderTerms = () => {
    const lines =
      USER_TERMS_OF_SERVICE.split('\n');

    return lines.map(
      (rawLine, index) => {
        const line = rawLine.trim();

        const anchorId =
          anchorByHeading[line];

        const isBlank =
          line === '';

        const isInternalTitle =
          line ===
          'TRIBE TERMS OF SERVICE (USERS)';

        const isEffectiveDate =
          line.startsWith(
            'Effective Date:'
          );

        const isNumberedHeading =
          /^\d+\.\s+[A-Z]/.test(line);

        const isLetterHeading =
          /^[A-D]\.\s+[A-Z]/.test(line);

        const isProgramHeading =
          line ===
          'TRIBE SMS PROGRAM';

        const isImportant =
          line.startsWith(
            'IMPORTANT:'
          ) ||
          line ===
            'PLEASE READ CAREFULLY.' ||
          line ===
            'PLEASE READ THIS SECTION CAREFULLY.' ||
          line.startsWith(
            'Reply STOP'
          ) ||
          line.startsWith(
            'Reply HELP'
          );

        const isBullet =
          line.startsWith('- ');

        if (isBlank) {
          return (
            <div
              key={`blank-${index}`}
              style={{ height: 9 }}
            />
          );
        }

        return (
          <React.Fragment
            key={`${index}-${line}`}
          >
            {anchorId && (
              <span
                id={anchorId}
                style={{
                  display: 'block',
                  scrollMarginTop: 100,
                }}
              />
            )}

            {isInternalTitle ? (
              <div
                style={
                  S.legalDocumentTitle
                }
              >
                {line}
              </div>
            ) : isEffectiveDate ? (
              <div
                style={
                  S.legalEffectiveDate
                }
              >
                {line}
              </div>
            ) : isNumberedHeading ? (
              <div
                style={
                  S.legalSectionHeading
                }
              >
                {line}
              </div>
            ) : isLetterHeading ? (
              <div
                style={S.legalSubheading}
              >
                {line}
              </div>
            ) : isProgramHeading ? (
              <div
                style={
                  S.legalProgramHeading
                }
              >
                {line}
              </div>
            ) : isImportant ? (
              <div
                style={S.legalImportant}
              >
                {line}
              </div>
            ) : isBullet ? (
              <div
                style={S.legalBullet}
              >
                <span
                  style={S.bulletMark}
                >
                  –
                </span>

                <span>
                  {line.slice(2)}
                </span>
              </div>
            ) : (
              <div
                style={S.legalBodyLine}
              >
                {line}
              </div>
            )}
          </React.Fragment>
        );
      }
    );
  };

  const S = {
    wrap: {
      minHeight: '100vh',
      background: '#fff',
      color: '#0B1020',

      fontFamily:
        'Open Sans, system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial, sans-serif',
    },

    page: {
      width: '100%',
      maxWidth: 1180,

      margin: '0 auto',

      padding: '0 28px 64px',

      boxSizing: 'border-box',
    },

    // =================================
    // NAVBAR
    // =================================

    topNav: {
      position: 'sticky',
      top: 0,
      zIndex: 80,
      height: 76,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      background: '#fff',
    },

    // LEFT — LEGAL BREADCRUMBS
    navBreadcrumbs: {
      display: 'flex',
      alignItems: 'center',
      gap: 7,
      whiteSpace: 'nowrap',
      lineHeight: 1,
    },

    homeLink: {
      display: 'inline-flex',
      alignItems: 'center',
      gap: 2,
      color: '#6b7280',
      textDecoration: 'none',
      fontSize: '0.83rem',
      fontWeight: 600,
      lineHeight: 1,
    },

    backIcon: {
      fontSize: 18,
      marginLeft: -5,
    },

    navSlash: {
      color: '#d1d5db',
      fontSize: '0.78rem',
      fontWeight: 400,
      userSelect: 'none',
    },

    currentLegalLink: {
      color: '#0B1020',
      fontSize: '0.83rem',
      fontWeight: 700,
      lineHeight: 1,
    },

    otherLegalLink: {
      color: '#9ca3af',
      textDecoration: 'none',
      fontSize: '0.83rem',
      fontWeight: 500,
      lineHeight: 1,
    },

    legalHeading: {
      marginLeft: 'auto',

      // EXACT SAME STYLE AS EDIT PROFILE HEADER
       fontSize: '0.95rem',
  fontWeight: 900,
  lineHeight: 1,
  textTransform: 'uppercase',
  letterSpacing: 0.2,
    },

    // =================================
    // DOCUMENT LAYOUT
    // =================================

    layout: {
      display: 'grid',

      gridTemplateColumns:
        showSideNav
          ? '180px minmax(0, 860px)'
          : 'minmax(0, 860px)',

      columnGap: 54,

      alignItems: 'start',
    },

    sidePlaceholder: {
      width: 180,
    },

    // =================================
    // FIXED SECTION NAV
    // =================================

    sideNav: {
      position: 'fixed',

      left:
        'max(28px, calc(50vw - 590px))',

      top: 112,

      width: 180,

      zIndex: 20,
    },

    sideItems: {
      display: 'flex',
      flexDirection: 'column',

      alignItems: 'flex-start',

      gap: 11,
    },

    sideItem: {
      appearance: 'none',

      border: 0,
      background: 'transparent',

      padding: 0,
      margin: 0,

      fontFamily: 'inherit',

      fontSize: '0.79rem',
      lineHeight: 1.3,

      textAlign: 'left',

      cursor: 'pointer',

      transition:
        'color 140ms ease, opacity 140ms ease, font-weight 140ms ease',
    },

    // =================================
    // DOCUMENT
    // =================================

    content: {
      minWidth: 0,
      paddingTop: 22,
    },

    h1: {
      margin: 0,
      marginBottom: 8,

      fontSize: '1.85rem',
      fontWeight: 700,

      lineHeight: 1.15,

      letterSpacing: '-0.025em',
    },

    small: {
      color: '#8b8f96',
      fontSize: '0.78rem',
    },

    // =================================
    // LEGAL COPY
    // =================================

    legalDocumentTitle: {
      marginTop: 25,
      marginBottom: 2,

      color: '#111',

      fontSize: 13,
      fontWeight: 700,

      lineHeight: 1.5,
    },

    legalEffectiveDate: {
      marginBottom: 14,

      color: '#92969d',

      fontSize: 12,

      lineHeight: 1.5,
    },

    legalSectionHeading: {
      marginTop: 21,
      marginBottom: 5,

      color: '#111',

      fontSize: 13.5,
      fontWeight: 700,

      lineHeight: 1.45,
    },

    legalSubheading: {
      marginTop: 13,
      marginBottom: 4,

      color: '#242424',

      fontSize: 13,
      fontWeight: 700,

      lineHeight: 1.5,
    },

    legalProgramHeading: {
      marginTop: 15,
      marginBottom: 5,

      color: '#111',

      fontSize: 13,
      fontWeight: 700,

      lineHeight: 1.5,
    },

    legalImportant: {
      margin: '2px 0',

      color: '#222',

      fontSize: 12.5,
      fontWeight: 700,

      lineHeight: 1.5,
    },

    legalBodyLine: {
      color: '#414141',

      fontSize: 13,
      fontWeight: 400,

      lineHeight: 1.58,
    },

    legalBullet: {
      display: 'flex',
      alignItems: 'flex-start',

      gap: 7,

      paddingLeft: 5,

      color: '#414141',

      fontSize: 13,

      lineHeight: 1.55,
    },

    bulletMark: {
      color: '#a8abb0',
      flexShrink: 0,
    },

    // =================================
    // SMS CONSENT EVIDENCE
    // =================================

    hr: {
      border: 0,
      borderTop:
        '1px solid #eee',

      margin: '32px 0',
    },

    h2: {
      marginTop: 0,
      marginBottom: 8,

      fontSize: '1.12rem',
      fontWeight: 700,

      lineHeight: 1.3,
    },

    smsIntro: {
      maxWidth: 720,

      margin: '8px 0 20px',

      color: '#4b5563',

      fontSize: 13,

      lineHeight: 1.58,
    },

    screenshotGrid: {
      display: 'grid',

      gridTemplateColumns:
        'repeat(auto-fit, minmax(220px, 1fr))',

      gap: 24,

      alignItems: 'start',

      marginTop: 20,
      marginBottom: 24,
    },

    screenshotCard: {
      minWidth: 0,
    },

    stepLabel: {
      marginBottom: 8,

      color: '#8b8f96',

      fontSize: '0.72rem',
      fontWeight: 700,

      textTransform: 'uppercase',

      letterSpacing: '0.04em',

      textAlign: 'center',
    },

    screenshot: {
      display: 'block',

      width: '100%',
      maxWidth: '260px',

      margin: '0 auto',

      background: '#fff',

      border:
        '1px solid #ddd',

      borderRadius: 12,
    },

    caption: {
      marginTop: 10,

      color: '#6b7280',

      fontSize: '0.78rem',

      lineHeight: 1.5,

      textAlign: 'center',
    },

    verificationNote: {
      marginTop: 16,

      color: '#6b7280',

      fontSize: '0.8rem',

      lineHeight: 1.55,
    },
  };

  return (
    <main style={S.wrap}>
      <div style={S.page}>

        {/* =================================
            NAVBAR
        ================================= */}
        <header style={S.topNav}>

          {/* LEFT */}
          <nav
            style={S.navBreadcrumbs}
            aria-label="Legal pages"
          >
            <Link
              to="/"
              style={S.homeLink}
            >
              <IoIosArrowBack
                style={S.backIcon}
              />

              Home
            </Link>

            {!compactNav && (
              <>
                <span style={S.navSlash}>
                  /
                </span>

                <span
                  style={S.currentLegalLink}
                >
                  Terms & Conditions
                </span>

                <span style={S.navSlash}>
                  /
                </span>

                <Link
                  to="/privacy"
                  style={S.otherLegalLink}
                >
                  Privacy Policy
                </Link>
              </>
            )}
          </nav>

          {/* RIGHT */}
          <div style={S.legalHeading}>
            Legal
          </div>
        </header>

        {/* =================================
            FIXED SECTION NAV
        ================================= */}
        {showSideNav && (
          <aside style={S.sideNav}>
            <nav
              style={S.sideItems}
              aria-label="Terms sections"
            >
              {PAGE_SECTIONS.map(
                (section) => {
                  const active =
                    activeSection ===
                    section.id;

                  return (
                    <button
                      key={section.id}
                      type="button"
                      onClick={() =>
                        goToSection(
                          section.id
                        )
                      }
                      style={{
                        ...S.sideItem,

                        color: active
                          ? '#111'
                          : '#b4b7bc',

                        fontWeight: active
                          ? 700
                          : 400,

                        opacity: active
                          ? 1
                          : 0.82,
                      }}
                    >
                      {section.label}
                    </button>
                  );
                }
              )}
            </nav>
          </aside>
        )}

        {/* =================================
            DOCUMENT
        ================================= */}
        <div style={S.layout}>
          {showSideNav && (
            <div
              style={
                S.sidePlaceholder
              }
            />
          )}

          <div style={S.content}>
            <span
              id="overview"
              style={{
                display: 'block',
                scrollMarginTop: 100,
              }}
            />

            <h1 style={S.h1}>
              Terms of Service
            </h1>

            <p style={S.small}>
              Last updated:{' '}
              {new Date().toLocaleDateString()}
            </p>

            <div>
              {renderTerms()}
            </div>

            <hr style={S.hr} />

            {/* =================================
                SMS CONSENT FLOW
            ================================= */}
            <section
              id="sms-consent"
              style={{
                scrollMarginTop: 100,
              }}
            >
              <h2 style={S.h2}>
                SMS Consent Flow
              </h2>

              <p style={S.smsIntro}>
                Tribe account registration is completed
                on a mobile device. Users first provide
                their mobile phone number and are then
                shown a separate SMS consent step. SMS
                consent is optional: users can select
                “No, not now” and continue registration
                without enabling text notifications.
                Users who have not enabled SMS may also
                be shown a separate optional SMS checkbox
                on the final registration step. Accepting
                Tribe’s Terms of Service and Privacy
                Policy and creating an account do not
                require SMS consent. Members can later
                manage Text Alerts from account settings.
              </p>

              <div
                style={
                  S.screenshotGrid
                }
              >
                {/* STEP 1 */}
                <div
                  style={
                    S.screenshotCard
                  }
                >
                  <div
                    style={S.stepLabel}
                  >
                    Step 1
                  </div>

                  <img
                    src={
                      SmsPhoneNumberScreenshot
                    }
                    alt="Tribe mobile registration phone number entry screen"
                    style={S.screenshot}
                  />

                  <p style={S.caption}>
                    The user provides the mobile
                    phone number associated with
                    their Tribe account before any
                    SMS consent choice is made.
                  </p>
                </div>

                {/* STEP 2 */}
                <div
                  style={
                    S.screenshotCard
                  }
                >
                  <div
                    style={S.stepLabel}
                  >
                    Step 2
                  </div>

                  <img
                    src={
                      SmsConsentDeclinedScreenshot
                    }
                    alt="Tribe SMS consent screen with No, not now selected and Continue enabled"
                    style={S.screenshot}
                  />

                  <p style={S.caption}>
                    SMS consent is presented
                    separately. “No, not now” is
                    selected and Continue remains
                    available, showing that users can
                    decline SMS and still continue
                    registration.
                  </p>
                </div>

                {/* STEP 3 */}
                <div
                  style={
                    S.screenshotCard
                  }
                >
                  <div
                    style={S.stepLabel}
                  >
                    Step 3
                  </div>

                  <img
                    src={
                      SmsTermsOptionalScreenshot
                    }
                    alt="Tribe final registration step with optional SMS checkbox unchecked, Terms accepted, and Create Account enabled"
                    style={S.screenshot}
                  />

                  <p style={S.caption}>
                The final registration step includes a separate optional SMS checkbox. Users can leave the SMS box unchecked while accepting the Terms and Privacy Policy and still create their account.
                  </p>
                </div>

                {/* STEP 4 */}
                <div
                  style={
                    S.screenshotCard
                  }
                >
                  <div
                    style={S.stepLabel}
                  >
                    Step 4
                  </div>

                  <img
                    src={
                      SmsSettingsOffScreenshot
                    }
                    alt="Tribe account settings with Text Alerts turned off"
                    style={S.screenshot}
                  />

                  <p style={S.caption}>
                    Members can later manage
                    Text Alerts from account settings,
                    including disabling text
                    notifications after registration.
                  </p>
                </div>
              </div>

              <p
                style={
                  S.verificationNote
                }
              >
                Tribe’s SMS consent disclosures explain
                that messages are recurring, message
                frequency varies, message and data rates
                may apply, and users may reply STOP to
                unsubscribe. The consent flow identifies
                the types of messages Tribe may send,
                including gathering recommendations,
                reservation reminders, and important
                gathering updates, and provides links to
                Tribe’s Terms of Service and Privacy
                Policy.
              </p>
            </section>

            <hr style={S.hr} />

            <p style={S.small}>
              Regional terms may apply
              depending on your location. If
              required by local law, you may
              have additional rights regarding
              your personal data.
            </p>
          </div>
        </div>
      </div>
    </main>
  );
}